import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import {
  Camera, Upload, ArrowRight, CheckCircle2, AlertCircle, Sparkles,
  Ruler, AudioLines, ImageIcon, Lock, Home, Layers, Wind, Wrench, Cable, PackageOpen, Search, X, Check,
} from 'lucide-react';
import axios from 'axios';
import QuoteResults from '../components/QuoteResults';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAKE_WEBHOOK_URL = process.env.REACT_APP_MAKE_WEBHOOK_URL || '';
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || '';
const WHATSAPP_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || '').replace(/\D/g, '');
const STORAGE_KEY = 'dc_estimator_last_result_v6';

const LOADING_STAGES = [
  { icon: Sparkles, text: 'AI analyzing room dimensions & wall layout...' },
  { icon: Ruler, text: 'Calculating heat-load capacity requirements...' },
  { icon: Wrench, text: 'Scoring installation complexity & access...' },
  { icon: ImageIcon, text: 'Rendering visualization of your new unit...' },
];

const pushLeadToWebhook = async (payload) => {
  if (!MAKE_WEBHOOK_URL) return;
  try {
    await fetch(MAKE_WEBHOOK_URL, {
      method: 'POST', mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) { console.warn('Webhook push failed:', e); }
};

// ---- Tier card ----
const TierCard = ({ value, active, title, sub, onClick, testid }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    data-testid={testid}
    className={`p-4 rounded-xl border-2 transition-all text-left ${active ? 'border-[#FF8C00] bg-[#FF8C00]/10' : 'border-white/10 hover:border-white/30'}`}
  >
    <p className="font-bold text-sm">{title}</p>
    <p className="text-xs text-gray-400 mt-1">{sub}</p>
  </button>
);

// ---- Searchable brand picker across all tiers ----
const TIER_GROUP_LABELS = {
  premium: 'Premium Installer Grade',
  mid: 'Smart Value / Mid-Range',
  entry: 'Budget / Entry',
};
const BrandPicker = ({ brands, selectedId, onSelect, onClear }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  const selected = brands.find(b => b.id === selectedId);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = useMemo(() => {
    const query = q.toLowerCase().trim();
    return brands.filter(b => !query || b.name.toLowerCase().includes(query));
  }, [brands, q]);

  const grouped = useMemo(() => {
    const out = { premium: [], mid: [], entry: [] };
    filtered.forEach(b => { if (out[b.tier]) out[b.tier].push(b); });
    return out;
  }, [filtered]);

  return (
    <div className="relative" ref={wrapRef} data-testid="brand-picker">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 bg-[#0d1829] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm hover:border-white/20 transition"
        data-testid="brand-picker-trigger"
      >
        <span className="flex items-center gap-2 truncate">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          {selected ? (
            <>
              <span className="truncate">{selected.name}</span>
              <span className="text-[10px] uppercase tracking-wider text-[#FF8C00]/80 bg-[#FF8C00]/10 px-1.5 py-0.5 rounded">
                {selected.tier === 'premium' ? 'Premium' : selected.tier === 'mid' ? 'Mid' : 'Entry'}
              </span>
            </>
          ) : (
            <span className="text-gray-400">Search and pick a specific brand...</span>
          )}
        </span>
        {selected && (
          <span
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="text-gray-500 hover:text-white p-0.5 cursor-pointer"
            data-testid="brand-picker-clear"
          >
            <X size={14} />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#121820] border border-white/15 rounded-xl shadow-2xl overflow-hidden">
          <div className="p-3 border-b border-white/10">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Type a brand name..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
                className="w-full bg-[#0d1829] border border-white/10 rounded-md pl-9 pr-3 py-2 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#FF8C00]/50"
                data-testid="brand-search-input"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {['premium', 'mid', 'entry'].map(tier => {
              const list = grouped[tier];
              if (!list?.length) return null;
              return (
                <div key={tier} className="px-2 py-2 border-b border-white/5 last:border-b-0">
                  <p className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-[#FF8C00]/85 font-bold">{TIER_GROUP_LABELS[tier]}</p>
                  {list.map(b => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => { onSelect(b); setOpen(false); setQ(''); }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm transition ${selectedId === b.id ? 'bg-[#FF8C00]/15 text-white' : 'text-gray-300 hover:bg-white/5'}`}
                      data-testid={`brand-option-${b.id}`}
                    >
                      <span>{b.name}</span>
                      {selectedId === b.id && <Check size={14} className="text-[#FF8C00]" />}
                    </button>
                  ))}
                </div>
              );
            })}
            {!filtered.length && (
              <p className="text-sm text-gray-500 text-center py-8">No brands match "{q}"</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Section heading ----
const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className="text-[#FF8C00]" />
      <h3 className="text-base uppercase tracking-[0.18em] text-gray-300 font-bold">{title}</h3>
    </div>
    <div className="space-y-5">{children}</div>
  </div>
);

const AIEstimator = () => {
  const [step, setStep] = useState('form');
  const [photos, setPhotos] = useState([]);        // [{ label, preview, b64 }, ...]
  const [outdoorPhoto, setOutdoorPhoto] = useState(null);  // { preview, b64 } | null
  const [stageIndex, setStageIndex] = useState(0);
  const roomInputRefs = useRef({});
  const outdoorInputRef = useRef(null);

  const [catalog, setCatalog] = useState({ brands: [], outdoorPlacementOptions: [], accessDifficultyOptions: [], propertyTypes: [] });

  const [formData, setFormData] = useState({
    // System
    roomsCount: 1,
    systemType: 'wall-split',
    cassetteSize: '600x600',
    // Equipment
    brandTier: 'entry',
    brandId: '',
    ownedEquipment: { brand: '', model: '', kWSize: '3.5kW', systemType: 'wall-split' },
    // Property
    propertyType: 'house',
    propertyFloors: 1,
    floorLevel: 0,
    // Outdoor
    outdoorPlacement: 'garden-patio',
    // Routing
    estimatedPipeRun: 3,
    commercialContainment: false,
    // Matrix ancillaries
    reuseExistingPipework: false,
    dedicatedElectricalFeed: false,
    condensateDrainPump: false,
  });
  const [results, setResults] = useState(null);
  const [gateForm, setGateForm] = useState({ name: '', email: '' });
  const [gateSubmitted, setGateSubmitted] = useState(false);

  // Catalog fetch
  useEffect(() => {
    axios.get(`${API}/estimator/catalog`).then(r => setCatalog(r.data)).catch(() => {});
  }, []);

  // Session restore — bumped to v6 because the photo shape is new
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const cached = JSON.parse(raw);
      if (cached?.results) {
        setResults(cached.results);
        setGateSubmitted(!!cached.gateSubmitted);
        if (cached.formData) setFormData(cached.formData);
        setStep(cached.results.status === 'DECLINED' ? 'declined' : (cached.gateSubmitted ? 'results' : 'gate'));
      }
    } catch (e) { /* ignore */ }
  }, []);

  const persistSession = (next) => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (step !== 'loading') return;
    setStageIndex(0);
    const id = setInterval(() => setStageIndex((i) => (i + 1) % LOADING_STAGES.length), 1800);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => { if (typeof window !== 'undefined') window.scrollTo(0, 0); }, [step]);

  // Resize photo array when roomsCount changes (preserve existing photos, append blanks)
  useEffect(() => {
    setPhotos(prev => {
      const target = formData.roomsCount;
      const next = [...prev];
      while (next.length < target) next.push({ label: `Room ${next.length + 1}`, preview: null, b64: null });
      while (next.length > target) next.pop();
      return next;
    });
  }, [formData.roomsCount]);

  const handleRoomPhoto = (idx) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotos(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], preview: reader.result, b64: reader.result.split(',')[1] };
        return next;
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRoomLabel = (idx, value) => {
    setPhotos(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], label: value };
      return next;
    });
  };

  const handleOutdoorPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setOutdoorPhoto({ preview: reader.result, b64: reader.result.split(',')[1] });
    reader.readAsDataURL(file);
  };

  // Roof placements: outdoor photo skippable for safety
  const isRoofPlacement = ['flat-roof-standard', 'flat-roof-crane'].includes(formData.outdoorPlacement);
  const outdoorRequired = !isRoofPlacement;

  const photosComplete = photos.length === formData.roomsCount && photos.every(p => p.b64);
  const outdoorComplete = outdoorRequired ? !!outdoorPhoto?.b64 : true;
  const canSubmit = photosComplete && outdoorComplete && formData.brandTier !== 'already-own';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStep('loading');
    try {
      const payload = {
        roomPhotos: photos.map(p => ({ label: p.label, imageBase64: p.b64 })),
        outdoorPhotoBase64: outdoorPhoto?.b64 || null,
        ...formData,
      };
      const response = await axios.post(`${API}/estimate-installation`, payload);
      setResults(response.data);
      const isDeclined = response.data.status === 'DECLINED';
      setGateSubmitted(false);
      setStep(isDeclined ? 'declined' : 'gate');
      persistSession({ results: response.data, formData, gateSubmitted: false });

      if (!isDeclined) {
        const p = response.data.pricing;
        pushLeadToWebhook({
          leadType: 'AI_ESTIMATOR',
          stage: 'GENERATED',
          source: 'website-ai-estimator',
          timestamp: new Date().toISOString(),
          quoteId: response.data.quoteId,
          quoteUrl: response.data.quoteId ? `${window.location.origin}/q/${response.data.quoteId}` : undefined,
          mockMode: !!response.data.mockMode,
          quoteMode: p?.quoteMode,
          complexityScore: p?.complexityScore,
          roomSize: `${p?.estimatedArea}m2`,
          recommendedUnit: p?.recommendedUnit,
          totalPrice: p?.totalPrice,
          rangeLow: p?.rangeLow,
          rangeHigh: p?.rangeHigh,
          brandLabel: p?.brandLabel,
          brandTier: p?.brandTier,
          alreadyOwn: !!p?.alreadyOwn,
          ...formData,
        });
      }
    } catch (error) {
      console.error('Estimation error:', error);
      alert('An error occurred. Please try again or call us on 020 8059 9941.');
      setStep('upload');
    }
  };

  const submitGate = async (e) => {
    e?.preventDefault?.();
    if (!gateForm.email || !gateForm.email.includes('@')) {
      alert('Please enter a valid email so we can send your quote.');
      return;
    }
    const p = results?.pricing;
    pushLeadToWebhook({
      leadType: 'AI_ESTIMATOR',
      stage: 'CONTACT_CAPTURED',
      source: 'website-ai-estimator',
      timestamp: new Date().toISOString(),
      quoteId: results?.quoteId,
      quoteUrl: results?.quoteId ? `${window.location.origin}/q/${results.quoteId}` : undefined,
      name: gateForm.name, email: gateForm.email,
      quoteMode: p?.quoteMode,
      complexityScore: p?.complexityScore,
      totalPrice: p?.totalPrice,
      rangeLow: p?.rangeLow,
      rangeHigh: p?.rangeHigh,
      brandLabel: p?.brandLabel,
      ...formData,
    });
    if (results?.quoteId) {
      try {
        await axios.post(`${API}/quotes/${results.quoteId}/event`, {
          type: 'CONTACT_CAPTURED', name: gateForm.name, email: gateForm.email,
        });
      } catch (e) { /* non-blocking */ }
    }
    setGateSubmitted(true);
    setStep('results');
    persistSession({ results, formData, gateSubmitted: true });
  };

  const skipGate = () => {
    setGateSubmitted(false);
    setStep('results');
    persistSession({ results, formData, gateSubmitted: false });
  };

  const handleWhatsApp = async () => {
    if (!results?.pricing) return;
    const p = results.pricing;
    const shareUrl = results.quoteId ? `${window.location.origin}/q/${results.quoteId}` : window.location.href;
    const priceLine = p.quoteMode === 'survey'
      ? `Preliminary range: £${p.rangeLow}–£${p.rangeHigh} (site survey recommended)`
      : p.quoteMode === 'range'
        ? `Estimated range: £${p.rangeLow}–£${p.rangeHigh}`
        : `Quoted price: £${p.totalPrice.toLocaleString()}`;
    const text = encodeURIComponent(
      `Hi Deep Control — AI estimate from your site:\n\n` +
      `• ${priceLine}\n` +
      `• Unit: ${p.recommendedUnit}\n` +
      `• Brand: ${p.brandLabel}\n` +
      `• Room: ~${Math.round(p.estimatedArea)}m²\n\n` +
      `Full quote: ${shareUrl}`
    );
    window.open(`${WHATSAPP_NUMBER ? `https://wa.me/${WHATSAPP_NUMBER}` : 'https://wa.me/'}?text=${text}`, '_blank', 'noopener,noreferrer');
    if (results?.quoteId) {
      try { await axios.post(`${API}/quotes/${results.quoteId}/event`, { type: 'WHATSAPP_SHARED' }); } catch (e) { /* */ }
    }
  };

  const handleBookSurvey = () => {
    if (CALENDLY_URL) window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    else window.location.href = '/contact';
  };

  const resetAll = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setStep('form');
    setPhotos([]);
    setOutdoorPhoto(null);
    setResults(null);
    setGateForm({ name: '', email: '' });
    setGateSubmitted(false);
  };

  const shareUrl = results?.quoteId ? `${typeof window !== 'undefined' ? window.location.origin : ''}/q/${results.quoteId}` : '';

  const showOwnedSubform = formData.brandTier === 'already-own';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d1829] via-[#0f1b2d] to-[#081220] py-16 lg:py-24" data-testid="ai-estimator-page">
      <SEO
        title="AI HVAC Estimator — Instant Quote in 60 Seconds"
        description="Upload a photo of your room and get an instant, AI-powered air conditioning installation quote with realistic visualisation. Free, no obligation, all-inclusive pricing."
        path="/ai-estimator"
      />
      <div className="max-w-6xl mx-auto px-6 lg:px-8">

        {step === 'upload' && (
          <div data-testid="estimator-upload-step">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF8C00] bg-[#FF8C00]/10 border border-[#FF8C00]/30 rounded-full px-4 py-1.5 mb-5">
                <Sparkles size={14} /> Step 2 of 2 — Photos
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3 leading-tight">Now show us your space</h1>
              <p className="text-gray-300 text-base sm:text-lg max-w-2xl mx-auto">
                One photo per room you want cooled, plus one of where the outdoor unit will sit.
                {' '}Stand back, get the full wall in frame, good lighting.
              </p>
            </div>

            {/* Room photos — N boxes driven by formData.roomsCount */}
            <div className="space-y-4 mb-8">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 font-bold">Indoor unit locations ({formData.roomsCount} {formData.roomsCount === 1 ? 'room' : 'rooms'})</p>
              {photos.map((photo, idx) => (
                <div key={idx} className="bg-[#121820]/70 border border-white/10 rounded-2xl p-4 sm:p-5" data-testid={`room-upload-${idx}`}>
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <div className="w-full sm:w-48 flex-shrink-0">
                      {photo.preview ? (
                        <img src={photo.preview} alt={photo.label} className="w-full h-32 object-cover rounded-lg border border-white/10" />
                      ) : (
                        <button
                          onClick={() => roomInputRefs.current[idx]?.click()}
                          className="w-full h-32 border-2 border-dashed border-[#FF8C00]/40 rounded-lg bg-[#0d1829]/60 hover:border-[#FF8C00] hover:bg-[#FF8C00]/[0.04] transition flex flex-col items-center justify-center gap-1"
                          data-testid={`room-upload-btn-${idx}`}
                        >
                          <Camera size={22} className="text-[#FF8C00]" />
                          <span className="text-xs text-gray-300 font-semibold">Add photo</span>
                        </button>
                      )}
                      <input
                        ref={el => (roomInputRefs.current[idx] = el)}
                        type="file" accept="image/*" capture="environment"
                        onChange={handleRoomPhoto(idx)} className="hidden"
                        data-testid={`room-file-input-${idx}`}
                      />
                    </div>
                    <div className="flex-1 w-full">
                      <Label className="text-white text-sm mb-2 block">Room name (optional)</Label>
                      <Input
                        type="text" value={photo.label}
                        onChange={(e) => handleRoomLabel(idx, e.target.value)}
                        placeholder={`Room ${idx + 1}`}
                        className="bg-[#0d1829] border-white/10 text-white mb-3"
                        data-testid={`room-label-${idx}`}
                      />
                      {photo.preview && (
                        <button
                          onClick={() => roomInputRefs.current[idx]?.click()}
                          className="text-xs text-gray-400 hover:text-[#FF8C00] underline"
                          data-testid={`room-reupload-${idx}`}
                        >
                          Replace photo
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Outdoor photo */}
            <div className="bg-[#121820]/70 border border-white/10 rounded-2xl p-4 sm:p-5 mb-8" data-testid="outdoor-upload-block">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-400 font-bold">Outdoor condenser location</p>
                {!outdoorRequired && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-400">Skippable (roof)</span>
                )}
              </div>
              {isRoofPlacement && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="outdoor-roof-safety">
                  <AlertCircle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-200 leading-relaxed">
                    <strong>Do not climb onto the roof.</strong> If safe, take the photo from a window or from the ground.
                    Skip this step if the location is unreachable — we'll confirm on the site survey.
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <div className="w-full sm:w-48 flex-shrink-0">
                  {outdoorPhoto?.preview ? (
                    <img src={outdoorPhoto.preview} alt="Outdoor placement" className="w-full h-32 object-cover rounded-lg border border-white/10" />
                  ) : (
                    <button
                      onClick={() => outdoorInputRef.current?.click()}
                      className="w-full h-32 border-2 border-dashed border-[#FF8C00]/40 rounded-lg bg-[#0d1829]/60 hover:border-[#FF8C00] hover:bg-[#FF8C00]/[0.04] transition flex flex-col items-center justify-center gap-1"
                      data-testid="outdoor-upload-btn"
                    >
                      <Wind size={22} className="text-[#FF8C00]" />
                      <span className="text-xs text-gray-300 font-semibold">{outdoorRequired ? 'Add photo' : 'Add photo (optional)'}</span>
                    </button>
                  )}
                  <input
                    ref={outdoorInputRef} type="file" accept="image/*" capture="environment"
                    onChange={handleOutdoorPhoto} className="hidden"
                    data-testid="outdoor-file-input"
                  />
                </div>
                <div className="flex-1 text-sm text-gray-400 leading-relaxed">
                  <p className="mb-2"><strong className="text-gray-200">Where will the outdoor unit sit?</strong></p>
                  <p className="text-xs">
                    {isRoofPlacement
                      ? 'Photo from a safe vantage point only. Optional — site survey will verify.'
                      : 'Photo of the garden / wall / patio where the condenser goes — include some surroundings for context.'}
                  </p>
                  {outdoorPhoto && (
                    <button onClick={() => outdoorInputRef.current?.click()} className="text-xs text-gray-400 hover:text-[#FF8C00] underline mt-2" data-testid="outdoor-reupload">
                      Replace photo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
              <Button onClick={() => setStep('form')} className="bg-transparent border-2 border-white/20 hover:bg-white/10 text-white w-full sm:w-auto" data-testid="estimator-back-to-form">
                ← Back to details
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="bg-[#FF8C00] hover:bg-[#e67e00] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-8 py-6 w-full sm:w-auto"
                data-testid="estimator-submit-btn"
              >
                Generate AI Pre-Survey <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
            {!canSubmit && (
              <p className="text-xs text-gray-500 mt-3 text-center" data-testid="submit-blocker-hint">
                {!photosComplete ? `Add photos for all ${formData.roomsCount} ${formData.roomsCount === 1 ? 'room' : 'rooms'}` :
                 !outdoorComplete ? 'Add an outdoor location photo (or change placement to roof to skip)' :
                 formData.brandTier === 'already-own' ? 'Customer-supplied equipment routes to site survey' : ''}
              </p>
            )}
          </div>
        )}

        {step === 'form' && (
          <div data-testid="estimator-form-step">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF8C00] bg-[#FF8C00]/10 border border-[#FF8C00]/30 rounded-full px-4 py-1.5 mb-5">
                <Sparkles size={14} /> Step 1 of 2 — Your details
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3 leading-tight">Tell us about your project</h1>
              <p className="text-gray-300 text-base sm:text-lg max-w-2xl mx-auto">
                A few quick questions so we know how many photos to ask for next.
              </p>
            </div>

            <div className="bg-[#121820]/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 space-y-8 max-w-3xl mx-auto">

              {/* Section 1 — Property */}
              <Section icon={Home} title="Your property">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white mb-2 block text-sm">Property type</Label>
                    <Select value={formData.propertyType} onValueChange={(v) => setFormData({ ...formData, propertyType: v })}>
                      <SelectTrigger className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-property-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121820] border-white/10 text-white">
                        {(catalog.propertyTypes || []).map(opt => <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Number of floors in property</Label>
                    <Select value={String(formData.propertyFloors)} onValueChange={(v) => setFormData({ ...formData, propertyFloors: parseInt(v) })}>
                      <SelectTrigger className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-property-floors"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121820] border-white/10 text-white">
                        <SelectItem value="1">1 floor</SelectItem>
                        <SelectItem value="2">2 floors</SelectItem>
                        <SelectItem value="3">3 floors</SelectItem>
                        <SelectItem value="4">4 floors</SelectItem>
                        <SelectItem value="5">5+ floors (townhouse / conversion)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Section>

              {/* Section 2 — System */}
              <Section icon={Layers} title="What you need">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white mb-2 block text-sm">Number of rooms to cool</Label>
                    <Select value={String(formData.roomsCount)} onValueChange={(v) => setFormData({ ...formData, roomsCount: parseInt(v) })}>
                      <SelectTrigger className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-rooms-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121820] border-white/10 text-white">
                        <SelectItem value="1">1 room (single split)</SelectItem>
                        <SelectItem value="2">2 rooms (multi-split)</SelectItem>
                        <SelectItem value="3">3 rooms (multi-split)</SelectItem>
                        <SelectItem value="4">4 rooms (multi-split)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block text-sm">System type</Label>
                    <Select value={formData.systemType} onValueChange={(v) => setFormData({ ...formData, systemType: v })}>
                      <SelectTrigger className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-system-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121820] border-white/10 text-white">
                        <SelectItem value="wall-split">Wall-mounted split</SelectItem>
                        <SelectItem value="cassette">Ceiling cassette</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.systemType === 'cassette' && (
                  <div>
                    <Label className="text-white mb-2 block text-sm">Cassette size</Label>
                    <Select value={formData.cassetteSize} onValueChange={(v) => setFormData({ ...formData, cassetteSize: v })}>
                      <SelectTrigger className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-cassette-size"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121820] border-white/10 text-white">
                        <SelectItem value="600x600">600 × 600 — residential (compact)</SelectItem>
                        <SelectItem value="900x900-commercial">900 × 900 — commercial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-white mb-2 block text-sm">Room floor level (where indoor unit goes)</Label>
                  <Select value={String(formData.floorLevel)} onValueChange={(v) => setFormData({ ...formData, floorLevel: parseInt(v) })}>
                    <SelectTrigger className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-floor-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121820] border-white/10 text-white">
                      <SelectItem value="0">Ground floor</SelectItem>
                      <SelectItem value="1">First floor</SelectItem>
                      <SelectItem value="2">Second floor (+£250 height access)</SelectItem>
                      <SelectItem value="3">Third floor or above (+£650 height access)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </Section>

              {/* Section 3 — Equipment preference */}
              <Section icon={PackageOpen} title="Equipment preference">
                <p className="text-xs text-gray-400 -mt-3">Pick a tier and let the installer choose, or search for a specific brand below.</p>
                <div className="grid grid-cols-3 gap-3">
                  <TierCard testid="tier-entry" value="entry" active={formData.brandTier === 'entry' && !formData.brandId}
                    title="Entry-level · £1,650 base" sub="Midea · Hisense · electriQ · Gree · Haier · AUX · TCL · Kaysun"
                    onClick={(v) => setFormData({ ...formData, brandTier: v, brandId: '' })} />
                  <TierCard testid="tier-mid" value="mid" active={formData.brandTier === 'mid' && !formData.brandId}
                    title="Mid-range · £1,950 base" sub="Samsung · Mitsubishi Heavy · LG · Bosch · Argo"
                    onClick={(v) => setFormData({ ...formData, brandTier: v, brandId: '' })} />
                  <TierCard testid="tier-premium" value="premium" active={formData.brandTier === 'premium' && !formData.brandId}
                    title="Premium · £2,400 base" sub="Daikin · Mitsubishi Electric · Fujitsu · Panasonic · Toshiba"
                    onClick={(v) => setFormData({ ...formData, brandTier: v, brandId: '' })} />
                </div>

                <div>
                  <Label className="text-white mb-2 block text-sm">Specific brand (optional)</Label>
                  <BrandPicker
                    brands={catalog.brands || []}
                    selectedId={formData.brandId}
                    onSelect={(b) => setFormData({ ...formData, brandId: b.id, brandTier: b.tier })}
                    onClear={() => setFormData({ ...formData, brandId: '' })}
                  />
                </div>

                <div className="pt-3 border-t border-white/5">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-2">Other options</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <button type="button" onClick={() => setFormData({ ...formData, brandTier: 'no-preference', brandId: '' })}
                      className={`p-3 rounded-lg border-2 text-left text-sm transition ${formData.brandTier === 'no-preference' ? 'border-[#FF8C00] bg-[#FF8C00]/10' : 'border-white/10 hover:border-white/30'}`}
                      data-testid="tier-nopref">
                      No preference — installer recommends
                    </button>
                    <button type="button" onClick={() => setFormData({ ...formData, brandTier: 'already-own', brandId: '' })}
                      className={`p-3 rounded-lg border-2 text-left text-sm transition ${formData.brandTier === 'already-own' ? 'border-[#FF8C00] bg-[#FF8C00]/10' : 'border-white/10 hover:border-white/30'}`}
                      data-testid="tier-own">
                      I already own the equipment (labour-only)
                    </button>
                  </div>
                </div>

                {showOwnedSubform && (
                  <div className="bg-[#0d1829] border border-white/10 rounded-xl p-4 space-y-3" data-testid="estimator-own-subform">
                    <p className="text-xs text-gray-400">Tell us about your equipment — we'll quote labour, routing and access only.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input placeholder="Brand (e.g. Daikin)" value={formData.ownedEquipment.brand}
                        onChange={(e) => setFormData({ ...formData, ownedEquipment: { ...formData.ownedEquipment, brand: e.target.value } })}
                        className="bg-[#0d1829] border-white/10 text-white text-sm" data-testid="own-brand" />
                      <Input placeholder="Model number" value={formData.ownedEquipment.model}
                        onChange={(e) => setFormData({ ...formData, ownedEquipment: { ...formData.ownedEquipment, model: e.target.value } })}
                        className="bg-[#0d1829] border-white/10 text-white text-sm" data-testid="own-model" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Select value={formData.ownedEquipment.kWSize} onValueChange={(v) => setFormData({ ...formData, ownedEquipment: { ...formData.ownedEquipment, kWSize: v } })}>
                        <SelectTrigger className="bg-[#0d1829] border-white/10 text-white text-sm" data-testid="own-kw"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121820] border-white/10 text-white">
                          <SelectItem value="2.5kW">2.5kW</SelectItem>
                          <SelectItem value="3.5kW">3.5kW</SelectItem>
                          <SelectItem value="5.0kW">5.0kW</SelectItem>
                          <SelectItem value="7.0kW">7.0kW</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={formData.ownedEquipment.systemType} onValueChange={(v) => setFormData({ ...formData, ownedEquipment: { ...formData.ownedEquipment, systemType: v } })}>
                        <SelectTrigger className="bg-[#0d1829] border-white/10 text-white text-sm" data-testid="own-type"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121820] border-white/10 text-white">
                          <SelectItem value="wall-split">Single split</SelectItem>
                          <SelectItem value="multi-split">Multi-split</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </Section>

              {/* Section 4 — Outdoor placement */}
              <Section icon={Wind} title="Outdoor condenser placement">
                <div>
                  <Label className="text-white mb-2 block text-sm">Where will the outdoor unit sit?</Label>
                  <Select value={formData.outdoorPlacement} onValueChange={(v) => setFormData({ ...formData, outdoorPlacement: v })}>
                    <SelectTrigger className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-outdoor-placement"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121820] border-white/10 text-white max-h-72">
                      {(catalog.outdoorPlacementOptions || []).map(opt => (
                        <SelectItem key={opt.id} value={opt.id}>
                          {opt.label}{opt.price ? ` (+£${opt.price})` : opt.triggersSurvey ? ' — survey' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </Section>

              {/* Section 5 — Pipe routing */}
              <Section icon={Ruler} title="Pipe routing">
                <div>
                  <Label className="text-white mb-2 block text-sm">
                    Estimated distance to outdoor unit: <span className="text-[#FF8C00]">{formData.estimatedPipeRun}m</span>
                  </Label>
                  <Slider value={[formData.estimatedPipeRun]} onValueChange={(value) => setFormData({ ...formData, estimatedPipeRun: value[0] })} min={1} max={20} step={1} className="py-4" data-testid="estimator-pipe-slider" />
                  <p className="text-xs text-gray-400">
                    ≤3m back-to-back = included · 3–7m = flat £280 · &gt;7m = £280 + £75 per extra metre.
                  </p>
                </div>
                <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10 hover:border-white/30 cursor-pointer transition" data-testid="estimator-commercial-tray-toggle">
                  <input
                    type="checkbox"
                    checked={formData.commercialContainment}
                    onChange={(e) => setFormData({ ...formData, commercialContainment: e.target.checked })}
                    className="mt-1 accent-[#FF8C00] w-4 h-4"
                  />
                  <div>
                    <p className="text-sm font-medium text-white">Commercial tray containment required</p>
                    <p className="text-xs text-gray-400">Galvanised steel cable tray / metal ducting — £125 per metre (replaces standard pipe charges)</p>
                  </div>
                </label>
              </Section>

              {/* Section 6 — Ancillaries (matrix toggles) */}
              <Section icon={Cable} title="Site-specific upgrades">
                <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10 hover:border-white/30 cursor-pointer transition" data-testid="estimator-reuse-pipework-toggle">
                  <input
                    type="checkbox"
                    checked={formData.reuseExistingPipework}
                    onChange={(e) => setFormData({ ...formData, reuseExistingPipework: e.target.checked })}
                    className="mt-1 accent-[#FF8C00] w-4 h-4"
                  />
                  <div>
                    <p className="text-sm font-medium text-white">Reuse existing pipework (flushed &amp; pressure-tested) <span className="text-emerald-400 text-xs ml-1">−£250 credit</span></p>
                    <p className="text-xs text-gray-400">Existing copper lines passed our flushing and pressure-test protocols.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10 hover:border-white/30 cursor-pointer transition" data-testid="estimator-dedicated-feed-toggle">
                  <input
                    type="checkbox"
                    checked={formData.dedicatedElectricalFeed}
                    onChange={(e) => setFormData({ ...formData, dedicatedElectricalFeed: e.target.checked })}
                    className="mt-1 accent-[#FF8C00] w-4 h-4"
                  />
                  <div>
                    <p className="text-sm font-medium text-white">Dedicated electrical feed required <span className="text-[#FF8C00] text-xs ml-1">+£380</span></p>
                    <p className="text-xs text-gray-400">Independent armoured mains from consumer board to weatherproof outdoor isolator (RCBO).</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10 hover:border-white/30 cursor-pointer transition" data-testid="estimator-drain-pump-toggle">
                  <input
                    type="checkbox"
                    checked={formData.condensateDrainPump}
                    onChange={(e) => setFormData({ ...formData, condensateDrainPump: e.target.checked })}
                    className="mt-1 accent-[#FF8C00] w-4 h-4"
                  />
                  <div>
                    <p className="text-sm font-medium text-white">Condensate drain pump required <span className="text-[#FF8C00] text-xs ml-1">+£195</span></p>
                    <p className="text-xs text-gray-400">Internal / basement rooms where gravity drainage isn't possible (Aspen / Mini Lime).</p>
                  </div>
                </label>
              </Section>

              <Button
                onClick={() => setStep('upload')}
                disabled={formData.brandTier === 'already-own'}
                className="w-full bg-[#FF8C00] hover:bg-[#e67e00] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-6 text-base"
                data-testid="estimator-next-photos-btn"
              >
                Next: upload photos <ArrowRight size={20} className="ml-2" />
              </Button>
              {formData.brandTier === 'already-own' && (
                <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-xl p-5 sm:p-6 text-left" data-testid="already-own-survey-block">
                  <div className="flex items-start gap-3 mb-3">
                    <AlertCircle size={22} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-amber-300 mb-1">We can't auto-quote customer-supplied equipment online</p>
                      <p className="text-xs text-gray-300 leading-relaxed">
                        Customer-supplied units introduce too many variables (capacity, bracket fit, missing parts, no hardware warranty) for a reliable online quote.
                        A short site survey locks in a fixed labour-only price on the day.
                      </p>
                    </div>
                  </div>
                  <a
                    href={CALENDLY_URL || 'tel:02080599941'}
                    target={CALENDLY_URL ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold px-5 py-2.5 rounded-lg text-sm transition"
                    data-testid="already-own-survey-cta"
                  >
                    Request a site survey <ArrowRight size={16} />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 'loading' && (
          <div className="max-w-2xl mx-auto py-16" data-testid="estimator-loading-step">
            <div className="bg-[#121820]/80 backdrop-blur-sm border border-white/10 rounded-2xl p-10">
              <div className="flex flex-col items-center mb-10">
                <div className="relative w-24 h-24 mb-6">
                  <div className="absolute inset-0 rounded-full border-2 border-[#FF8C00]/20" />
                  <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#FF8C00] animate-spin" />
                  <div className="absolute inset-3 rounded-full bg-[#FF8C00]/10 grid place-items-center"><Sparkles size={28} className="text-[#FF8C00]" /></div>
                </div>
                <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-2">Engineering in progress</p>
                <p className="text-sm text-gray-500">Usually completes in 3–5 seconds</p>
              </div>
              <ul className="space-y-4">
                {LOADING_STAGES.map((s, i) => {
                  const done = i < stageIndex;
                  const active = i === stageIndex;
                  const Icon = s.icon;
                  return (
                    <li key={i} className={`flex items-start gap-4 p-3 rounded-lg transition-all ${active ? 'bg-[#FF8C00]/10 border border-[#FF8C00]/30' : 'opacity-60'}`} data-testid={`estimator-loading-stage-${i}`}>
                      <div className={`mt-0.5 w-6 h-6 rounded-full grid place-items-center flex-shrink-0 ${done ? 'bg-[#FF8C00] text-white' : active ? 'bg-[#FF8C00]/30 text-[#FF8C00]' : 'bg-white/10 text-white/40'}`}>
                        {done ? <CheckCircle2 size={14} /> : <Icon size={14} />}
                      </div>
                      <p className={`text-sm leading-relaxed ${active ? 'text-white' : 'text-gray-400'}`}>{s.text}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {step === 'gate' && results && (
          <div className="relative" data-testid="estimator-gate-step">
            <div className="absolute inset-0 pointer-events-none select-none" aria-hidden>
              <div className="opacity-30 blur-[6px] scale-[0.98]">
                <div className="bg-gradient-to-br from-[#FF8C00]/20 to-[#FF8C00]/5 border-2 border-[#FF8C00]/50 rounded-2xl p-8 mb-8 text-center">
                  <p className="text-sm uppercase tracking-[0.25em] text-gray-300 mb-2">Your Estimate</p>
                  <p className="text-7xl font-extrabold text-[#FF8C00] mb-5">£•••</p>
                </div>
              </div>
            </div>
            <div className="relative max-w-xl mx-auto">
              <div className="bg-[#121820]/95 backdrop-blur-xl border border-[#FF8C00]/30 rounded-2xl p-8 sm:p-10 shadow-2xl">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#FF8C00]/15 grid place-items-center mb-4">
                    <Lock size={24} className="text-[#FF8C00]" />
                  </div>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#FF8C00] mb-2">Your AI estimate is ready</p>
                  <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">Unlock your full installation quote</h2>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    Enter your details to see your full price, breakdown and AR preview — we'll also email you a copy.
                  </p>
                </div>
                <form onSubmit={submitGate} className="space-y-4">
                  <div>
                    <Label className="text-white mb-2 block text-sm">Full name</Label>
                    <Input type="text" value={gateForm.name} onChange={(e) => setGateForm({ ...gateForm, name: e.target.value })} placeholder="Jane Smith" className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-gate-name" />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block text-sm">Email <span className="text-[#FF8C00]">*</span></Label>
                    <Input type="email" required value={gateForm.email} onChange={(e) => setGateForm({ ...gateForm, email: e.target.value })} placeholder="jane@example.com" className="bg-[#0d1829] border-white/10 text-white" data-testid="estimator-gate-email" />
                  </div>
                  <Button type="submit" className="w-full bg-[#FF8C00] hover:bg-[#e67e00] text-white font-bold py-6 text-base" data-testid="estimator-gate-submit">
                    Reveal my full quote <ArrowRight size={20} className="ml-2" />
                  </Button>
                </form>
                <div className="mt-6 text-center">
                  <button type="button" onClick={skipGate} className="text-xs text-gray-500 hover:text-gray-300 underline underline-offset-4" data-testid="estimator-gate-skip">
                    Skip for now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 'declined' && results && (
          <div className="max-w-3xl mx-auto text-center py-8" data-testid="estimator-declined-step">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#FF8C00]/10 flex items-center justify-center"><AlertCircle size={40} className="text-[#FF8C00]" /></div>
            <h2 className="text-4xl font-bold mb-6">Complex System Detected</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-8">{results.message}</p>
            <Button onClick={() => (window.location.href = '/contact')} className="bg-[#FF8C00] hover:bg-[#e67e00] text-white font-bold px-8 py-6">
              Book site survey
            </Button>
          </div>
        )}

        {step === 'results' && results && (
          <QuoteResults
            pricing={results.pricing}
            narrative={results.narrative}
            rooms={(results.rooms || []).map((r, i) => ({
              ...r,
              // Backend response doesn't ship the original photo back — use the local preview
              originalImage: photos[i]?.preview || null,
            }))}
            outdoor={results.outdoor ? {
              ...results.outdoor,
              originalImage: outdoorPhoto?.preview || null,
            } : null}
            outdoorImageUrl={outdoorPhoto?.preview || null}
            mockMode={!!results.mockMode}
            shareUrl={shareUrl}
            isSharedView={false}
            onWhatsApp={handleWhatsApp}
            onBookSurvey={handleBookSurvey}
            onReset={resetAll}
            onCopyLink={null}
          />
        )}
      </div>
    </div>
  );
};

export default AIEstimator;
